/**
 * Light or dark. The console opens in DEFAULT_THEME (dark) until the person picks Light, Dark or Follow Windows from the
 * rail; the pick is kept in this browser only (a convenience, not a setting anyone else sees). tokens.css does the
 * switching: no `data-theme` follows prefers-color-scheme, `data-theme="light"` or `"dark"` pins it.
 */
import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';

export type ThemePreference = 'system' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

/**
 * The console's default before a person picks one: one value (owner decision 2026-10-11 09:40: dark; the rail's switch
 * still offers light and following Windows). index.html carries the same value on <html> so the first paint is right,
 * and public/theme.js (loaded in <head>, before anything is drawn) applies a pin kept in this browser under
 * STORAGE_KEY before that first paint; test/shell-theme.test.ts keeps the three in step.
 */
export const DEFAULT_THEME: ThemePreference = 'dark';

/** Where the pin is kept in this browser; public/theme.js reads the same key. */
export const STORAGE_KEY = 'gyro-admin.theme';
const DARK_QUERY = '(prefers-color-scheme: dark)';

const isPreference = (value: unknown): value is ThemePreference => value === 'system' || value === 'light' || value === 'dark';

/** The person's pin, or the default when none is stored (or storage is blocked). */
export function readPreference(): ThemePreference {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (isPreference(stored)) return stored;
  } catch {
    // Storage blocked (private window, policy): the default applies.
  }
  return DEFAULT_THEME;
}

function storePreference(preference: ThemePreference): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, preference);
  } catch {
    // Not kept past this page; it still applies now.
  }
}

/** Writes the preference onto <html>: `data-theme` for a pin, nothing for the system setting. */
export function applyPreference(preference: ThemePreference, root: HTMLElement = document.documentElement): void {
  if (preference === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', preference);
}

/** Which palette shows for a preference, given whether the system asks for dark. */
export function resolveTheme(preference: ThemePreference, systemDark: boolean): ResolvedTheme {
  if (preference === 'light' || preference === 'dark') return preference;
  return systemDark ? 'dark' : 'light';
}

function subscribeSystem(listener: () => void): () => void {
  const media = typeof window.matchMedia === 'function' ? window.matchMedia(DARK_QUERY) : null;
  media?.addEventListener?.('change', listener);
  return () => media?.removeEventListener?.('change', listener);
}

function systemDark(): boolean {
  return typeof window.matchMedia === 'function' ? window.matchMedia(DARK_QUERY).matches : false;
}

/** The person's preference, what it resolves to now, and a setter that applies and keeps it. */
export function useTheme(): { preference: ThemePreference; resolved: ResolvedTheme; setPreference: (preference: ThemePreference) => void } {
  const [preference, setState] = useState<ThemePreference>(readPreference);
  const dark = useSyncExternalStore(subscribeSystem, systemDark, () => false);
  useEffect(() => {
    applyPreference(preference);
  }, [preference]);
  const setPreference = useCallback((next: ThemePreference) => {
    storePreference(next);
    setState(next);
  }, []);
  return { preference, resolved: resolveTheme(preference, dark), setPreference };
}
